import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import i18next from 'i18next';
import zhConvertor from 'zhconvertor';
import SeatBoundaryMap from './SeatBoundaryMap';
import { recordSeat } from '../../utils/interestTracker';
import './SwingSeats.css';

// Chinese is stored traditional (繁體) and converted to simplified by tr() unless
// the site language is zh_TW — matching SeatInfoPanel / johor.jsx.
const tr = (text) =>
  localStorage.getItem('lang') !== 'zh_TW' ? zhConvertor.t2s(text) : text;

// Curated Johor swing seats. Names mirror johor.jsx SEAT_NAMES (traditional `c`,
// romanised-Malay `e`) so the cards render instantly before the API responds.
const SWING_SEATS = [
  { code: 'N7', c: '武吉哈逢', e: 'Bukit Kepong' },
  { code: 'N51', c: '武吉峇都', e: 'Bukit Batu' },
  { code: 'N10', c: '東甲', e: 'Tangkak' },
  { code: 'N11', c: '實廊', e: 'Serom' },
  { code: 'N8', c: '武吉巴西', e: 'Bukit Pasir' },
  { code: 'N24', c: '新加蘭', e: 'Senggarang' },
  { code: 'N19', c: '永平', e: 'Yong Peng' },
  { code: 'N55', c: '北干那那', e: 'Pekan Nenas' },
];

// Fixed state-level Johor election tag opened by every card (traditional →
// simplified via tr() per the current language).
const JOHOR_TAG = '柔佛州選（柔州選舉）';

// Editor logos take precedence; fall back to the party-icon convention URL.
const allianceIcon = (alliance_en, alliance_logo, party_logo) =>
  alliance_logo ||
  (alliance_en ? `https://sabah2025.orientaldaily.com.my/party/${alliance_en}.png` : '') ||
  party_logo || '';

// Independent (BEBAS) candidates show their dedicated logo in place of the
// alliance/party icon; otherwise fall back to the regular icon chain.
const seatIcon = (c) =>
  c && (c.party_eng || '').toUpperCase() === 'BEBAS' && c.bebas_logo
    ? c.bebas_logo
    : allianceIcon(c?.alliance_en, c?.alliance_logo, c?.party_logo);

// Before voting day (countdownTarget in the future, or unset) the map tile shows
// the previous holder's alliance colour; once the target passes it switches to
// the live current winner/leader (grey until results come in).
const isBeforeTarget = () => {
  const t =
    typeof window !== 'undefined' && window.countdownTarget
      ? new Date(window.countdownTarget)
      : null;
  if (!t || Number.isNaN(t.getTime())) return true;
  return Date.now() < t.getTime();
};

const winnerOf = (seat) =>
  seat && Array.isArray(seat.candidate)
    ? seat.candidate.find((c) => parseInt(c.status, 10) === 1)
    : null;

// Leading candidate for an in-progress current election (no winner yet): highest
// votes, only when some votes have been counted.
const leaderOf = (seat) => {
  if (!seat || !Array.isArray(seat.candidate) || seat.candidate.length === 0) return null;
  if (parseInt(seat.total_votes, 10) <= 0) return null;
  return seat.candidate.reduce((best, c) =>
    (parseInt(c.votes, 10) || 0) > (parseInt(best.votes, 10) || 0) ? c : best,
  );
};

// Candidate name / party text inside a row-body opens the related-news panel
// for that term (a name falls back to the party when it has no articles,
// matching CandidateBar). stopPropagation keeps the card's own JOHOR_TAG
// click from also firing.
const TagText = ({ className, text, fallbackTag, onTagClick }) => {
  if (!text) return null;
  const open = (e) => {
    e.stopPropagation();
    if (onTagClick) onTagClick({ tag: text, fallbackTag, location: 'swing_seats' });
  };
  return (
    <span
      className={className}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open(e);
        }
      }}
    >
      {text}
    </span>
  );
};

const SwingSeatCard = ({ seat, past, current, onClick, onTagClick }) => {
  const pastWinner = winnerOf(past);
  const pastColor = pastWinner ? (pastWinner.alliance_color || '#8c8c8c') : undefined;

  const currentWinner = winnerOf(current);
  const currentLeader = currentWinner || leaderOf(current);
  const currentColor = currentLeader
    ? (currentLeader.alliance_color || '#8c8c8c')
    : undefined;
  // Past holder before voting day, live leader after.
  const tileColor = isBeforeTarget() ? pastColor : currentColor;
  const hasCurrent = current && parseInt(current.total_votes, 10) > 0;
  const currentCandidates =
    current && Array.isArray(current.candidate) ? current.candidate : [];

  return (
    <div
      className="swing-seat-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="swing-seat-card__map">
        <SeatBoundaryMap seatCode={seat.code} fill={tileColor} />
        <span className="swing-seat-card__code">{seat.code}</span>
      </div>

      <div className="swing-seat-card__head">
        <span className="swing-seat-card__name">{tr(seat.c)}</span>
        <span className="swing-seat-card__ename">{seat.e}</span>
      </div>

      {window.hideCurrentContestant === false && 
      <div className="swing-seat-card__row swing-seat-card__row--current">
        <span className="swing-seat-card__row-label">
          {!hasCurrent && currentCandidates.length > 0 ? tr('本届候选人') : tr('本届')}
        </span>
        {hasCurrent && currentLeader ? (
          <span className="swing-seat-card__row-body">
            {seatIcon(currentLeader) && (
              <img
                className="swing-seat-card__icon"
                src={seatIcon(currentLeader)}
                alt=""
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <TagText
              className="swing-seat-card__cand"
              text={currentLeader.candidate_name}
              fallbackTag={currentLeader.party}
              onTagClick={onTagClick}
            />
            <TagText
              className="swing-seat-card__party"
              text={currentLeader.party}
              onTagClick={onTagClick}
            />
            {!currentWinner && (
              <span className="swing-seat-card__leading">{tr('暂时领先')}</span>
            )}
          </span>
        ) : currentCandidates.length > 0 ? (
          <span className="swing-seat-card__candlist">
            {currentCandidates.map((c, i) => (
              <span key={i} className="swing-seat-card__row-body">
                {seatIcon(c) && (
                  <img
                    className="swing-seat-card__icon"
                    src={seatIcon(c)}
                    alt=""
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <TagText
                  className="swing-seat-card__cand"
                  text={c.candidate_name}
                  fallbackTag={c.party}
                  onTagClick={onTagClick}
                />
                <TagText
                  className="swing-seat-card__party"
                  text={c.party}
                  onTagClick={onTagClick}
                />
              </span>
            ))}
          </span>
        ) : (
          <span className="swing-seat-card__muted">{tr('尚未开始')}</span>
        )}
      </div>}

      <div className="swing-seat-card__row swing-seat-card__row--past">
        <span className="swing-seat-card__row-label">{tr('上届')}</span>
        {pastWinner ? (
          <span className="swing-seat-card__row-body">
            {seatIcon(pastWinner) && (
              <img
                className="swing-seat-card__icon"
                src={seatIcon(pastWinner)}
                alt=""
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <TagText
              className="swing-seat-card__cand"
              text={pastWinner.candidate_name}
              fallbackTag={pastWinner.party}
              onTagClick={onTagClick}
            />
            <TagText
              className="swing-seat-card__party"
              text={pastWinner.party}
              onTagClick={onTagClick}
            />
          </span>
        ) : (
          <span className="swing-seat-card__muted">{tr('暂无资料')}</span>
        )}
        {pastWinner && parseInt(past.majority, 10) > 0 && (
          <span className="swing-seat-card__majority">
            {tr('多数票')} {parseInt(past.majority, 10).toLocaleString()}
          </span>
        )}
      </div>

      <Link
        className="swing-seat-card__button"
        to={`/seats/johor/n#s${seat.code}`}
        onClick={(e) => {
          e.stopPropagation();
          recordSeat({ slug: 'johor', seatname: seat.code, label: tr(seat.c) });
        }}
      >
        {tr('查看该议席')} →
      </Link>
    </div>
  );
};

// Both datasets come from Home's shared seatinfo fetches (current 15s poll +
// one-time last_election) — this section no longer issues its own requests.
const toMap = (rows) => {
  const out = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row && row.seatname) out[row.seatname] = row;
  });
  return out;
};

const SwingSeatsSection = ({ current, past, onSeatClick }) => {
  // seatname (e.g. "N7") -> seat record, for current and past elections.
  const currentBySeat = useMemo(() => toMap(current), [current]);
  const pastBySeat = useMemo(() => toMap(past), [past]);

  const tag = useMemo(() => tr(JOHOR_TAG), []);

  return (
    <section className="swing-seats" aria-labelledby="swing-seats-heading">
      <h2 id="swing-seats-heading" className="section-title">
        {i18next.t('摇摆州席') || '摇摆州席'}
      </h2>
      <div className="swing-seats__scroller">
        {SWING_SEATS.map((seat) => (
          <SwingSeatCard
            key={seat.code}
            seat={seat}
            past={pastBySeat[seat.code]}
            current={currentBySeat[seat.code]}
            onClick={() => onSeatClick && onSeatClick(tag)}
            onTagClick={onSeatClick}
          />
        ))}
      </div>
    </section>
  );
};

export default SwingSeatsSection;
